import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  AgeRange,
  Gender,
  InstantEventType,
  InstantStylingMatchStatus,
  MatchResultCode,
  PricingTier,
  StylingPreferenceRequest,
  Stylist,
  VideoCallStarted,
} from '@/shared/types/generated';
import { REASON_OPTIONS } from '@/components/bookConsultation/shared/constants';

interface RequestForm {
  duration: InstantEventType;
  pricingTier: PricingTier;
  reason: string;
  gender: Gender;
  age: AgeRange;
  style: StylingPreferenceRequest;
  details: string;
  declinedStylistIds: number[];
  /** Promo code validated on the review step, sent with the payment on match */
  discountCode: string | null;
  discountPercentage: number | null;
}

type ClientStoreStateProps = {
  isInstantStylingModalVisible: boolean;
  requestForm: RequestForm;
  videoCallStarted: VideoCallStarted | null;
  matchResultCode: MatchResultCode | null;
  matchedStylist: Stylist | null;
};

type StylistStoreStateProps = {
  isInstantStylingActive: boolean;
};

type SharedStoreStateProps = {
  matchStatus: InstantStylingMatchStatus | null;
};

const defaultClientState: ClientStoreStateProps = {
  isInstantStylingModalVisible: false,
  requestForm: {
    duration: InstantEventType.SixtyMinutes,
    pricingTier: PricingTier.Tier1,
    reason: REASON_OPTIONS[0].value,
    gender: Gender.PreferNotToSay,
    age: AgeRange.NoPreference,
    style: StylingPreferenceRequest.NoPreference,
    details: '',
    declinedStylistIds: [],
    discountCode: null,
    discountPercentage: null,
  },
  videoCallStarted: null,
  matchResultCode: null,
  matchedStylist: null,
};

const defaultMatchStatus: InstantStylingMatchStatus = {
  __typename: 'InstantStylingMatchStatus',
  requestId: 0,
  clientAccepted: false,
  clientUserId: 0,
  stylistAccepted: false,
  stylistUserId: 0,
  paymentId: 0,
  consultationId: 0,
  stylistDeclined: false,
  clientDeclined: false,
  clientCancelled: false,
};

const defaultStylistState: StylistStoreStateProps = {
  isInstantStylingActive: false,
};

const defaultSharedState: SharedStoreStateProps = {
  matchStatus: null,
};

type StoreActionProps = {
  // Client store actions
  setIsInstantStylingModalVisible: (visible: boolean) => void;
  setRequestForm: (form: Partial<RequestForm>) => void;
  setVideoCallStarted: (videoCallStarted: VideoCallStarted | null) => void;
  resetMatchStatus: () => void;
  resetMatchStatusKeepRequestId: () => void;
  setMatchResultCode: (matchResultCode: MatchResultCode | null) => void;
  setMatchedStylist: (matchedStylist: Stylist | null) => void;

  // Stylist store actions
  setIsInstantStylingActive: (active: boolean) => void;

  // Shared store actions
  setMatchStatus: (
    matchStatus: Partial<InstantStylingMatchStatus> | null,
  ) => void;
  resetClientAndSharedState: () => void;
  resetStore: () => void;
};

type StoreState = ClientStoreStateProps &
  StylistStoreStateProps &
  SharedStoreStateProps &
  StoreActionProps;

const useInstantStylingStore = create<StoreState>()(
  immer((set) => ({
    ...defaultClientState,
    ...defaultStylistState,
    ...defaultSharedState,

    // Client store actions
    setIsInstantStylingModalVisible: (visible) =>
      set((state) => {
        state.isInstantStylingModalVisible = visible;
      }),

    setRequestForm: (form) =>
      set((state) => {
        state.requestForm = {
          ...state.requestForm,
          ...form,
        };
      }),

    setVideoCallStarted: (videoCallStarted) =>
      set((state) => {
        state.videoCallStarted = videoCallStarted;
      }),

    resetMatchStatus: () =>
      set((state) => {
        state.matchStatus = defaultMatchStatus;
      }),

    resetMatchStatusKeepRequestId: () =>
      set((state) => {
        if (state.matchStatus) {
          const requestId = state.matchStatus.requestId;
          state.matchStatus = {
            ...defaultMatchStatus,
            requestId,
          };
        }
      }),

    setMatchResultCode: (matchResultCode) =>
      set((state) => {
        state.matchResultCode = matchResultCode;
      }),

    setMatchedStylist: (matchedStylist) =>
      set((state) => {
        state.matchedStylist = matchedStylist;
      }),

    // Stylist store actions
    setIsInstantStylingActive: (active) =>
      set((state) => {
        state.isInstantStylingActive = active;
      }),

    // Shared store actions
    setMatchStatus: (patch) =>
      set((state) => {
        if (patch === null) {
          state.matchStatus = null;
        } else {
          state.matchStatus = {
            ...defaultMatchStatus,
            ...(state.matchStatus ?? {}),
            ...patch,
          };
        }
      }),

    resetClientAndSharedState: () =>
      set((state) => {
        Object.assign(state, {
          ...defaultClientState,
          ...defaultSharedState,
        });
      }),

    resetStore: () =>
      set({
        ...defaultClientState,
        ...defaultStylistState,
        ...defaultSharedState,
      }),
  })),
);

export default useInstantStylingStore;
